import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, imgSrc, money, errMsg } from "@/lib/api";
import TabHeader from "@/components/admin/TabHeader";
import { field } from "@/pages/admin/AdminLogin";

const STATUSES = [
  ["paid", "Paid — to make / pack"], ["in_progress", "In progress"], ["shipped", "Shipped"],
  ["delivered", "Delivered"], ["cancelled", "Cancelled"], ["refunded", "Refunded"],
];
const fmtAddr = (a = {}) => [a.line1, a.line2, [a.city, a.state, a.postal_code].filter(Boolean).join(" "), a.country].filter(Boolean).join(", ");

function OrderCard({ o, onSaved }) {
  const [tracking, setTracking] = useState(o.tracking || "");
  const update = async (patch) => {
    try { await api.patch(`/admin/orders/${o.id}`, patch); toast.success("Order updated"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`admin-order-${o.id}`} className="bg-paper p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs text-clay">#{o.id}</p>
          <p className="mt-1 font-display text-2xl font-extralight text-ink">{o.customer?.name || "—"}</p>
          <p className="font-body text-xs text-muted">{o.customer?.email} {o.customer?.phone ? `· ${o.customer.phone}` : ""}</p>
          <p className="mt-1 font-body text-xs text-muted">{new Date(o.created_at).toLocaleString()}</p>
        </div>
        <div className="text-right">
          <p data-testid={`admin-order-total-${o.id}`} className="font-display text-2xl font-extralight text-ink">{money(o.total)}</p>
          <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={(e) => update({ status: e.target.value })} className={`${field} mt-2 w-auto`}>
            {o.status === "pending_payment" && <option value="pending_payment">Awaiting payment</option>}
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <ul className="mt-5 space-y-3 border-t border-hairline pt-4">
        {o.items.map((it, i) => (
          <li key={i} className="flex gap-3">
            <img src={imgSrc(it.image)} alt="" className="h-14 w-11 bg-[#333336] object-cover object-top" />
            <div className="font-body text-sm">
              <p className="text-ink">{it.qty} × {it.name} <span className="text-muted">({money(it.price)})</span></p>
              <p className="text-xs text-muted">{Object.values(it.options || {}).join(" · ")}</p>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4 grid gap-4 border-t border-hairline pt-4 font-body text-sm md:grid-cols-2">
        <div><p className="overline mb-1 text-[0.58rem] text-muted">Ship to</p><p className="text-ink">{fmtAddr(o.shipping_address) || "—"}</p></div>
        <div><p className="overline mb-1 text-[0.58rem] text-muted">Buyer’s note & measurements</p><p className="whitespace-pre-line text-ink">{o.note || "—"}</p></div>
      </div>
      {o.gift?.is_gift && (
        <div data-testid={`admin-order-gift-${o.id}`} className="mt-4 bg-[#FBF6F1] p-4 font-body text-sm text-ink">
          <p className="overline mb-1 text-[0.58rem] text-clay">Gift</p>
          <p>For: {o.gift.recipient || "—"}</p>
          <p className="whitespace-pre-line">“{o.gift.message || "—"}”</p>
        </div>
      )}
      {o.admin_note && <p className="mt-3 font-body text-sm text-red-700">{o.admin_note}</p>}
      <div className="mt-4 flex gap-2">
        <input data-testid={`admin-order-tracking-${o.id}`} value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number" className={field} />
        <button type="button" data-testid={`admin-order-tracking-save-${o.id}`} onClick={() => update({ tracking, status: o.status === "paid" || o.status === "in_progress" ? "shipped" : o.status })}
          className="shrink-0 bg-ink px-5 text-paper"><span className="overline">{o.status === "paid" || o.status === "in_progress" ? "Save & mark shipped" : "Save tracking"}</span></button>
      </div>
    </div>
  );
}

export default function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-orders-tab">
      <TabHeader title="Orders" note="Paid orders from the boutique. Make or pack the piece, ship it, then add the tracking number." />
      {orders && orders.length === 0 && <p data-testid="admin-orders-empty" className="font-display text-2xl font-extralight text-stone">No orders yet.</p>}
      <div className="space-y-4">{orders?.map((o) => <OrderCard key={o.id + o.status + (o.tracking || "")} o={o} onSaved={load} />)}</div>
    </div>
  );
}
